<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui'
import type { ContactFormSchemaType } from '~~/shared/validation'

import { ContactFormSchema } from '~~/shared/validation'

definePageMeta({ prerender: true })

const { data: contact } = await useAsyncData('contact', () => queryCollection('contact').first())

useSeoMeta({
  description: () => contact.value?.seo.description,
  ogDescription: () => contact.value?.seo.ogDescription ?? contact.value?.seo.description,
  ogTitle: () => contact.value?.seo.ogTitle ?? contact.value?.seo.title,
  title: () => contact.value?.seo.title,
  twitterDescription: () => contact.value?.seo.ogDescription ?? contact.value?.seo.description,
  twitterTitle: () => contact.value?.seo.ogTitle ?? contact.value?.seo.title,
})

defineOgImageComponent('Default', {
  description: () => contact.value?.seo.ogDescription ?? contact.value?.seo.description ?? '',
  eyebrow: 'signalfire.games / contact',
  title: 'Contact',
})

const siteConfig = useSiteConfig()
useSchemaOrg([
  ...(contact.value?.lastUpdated
    ? [ defineWebPage({ dateModified: contact.value.lastUpdated }) ]
    : []),
  defineBreadcrumb({
    itemListElement: [
      { item: siteConfig.url, name: 'Home' },
      { item: `${siteConfig.url}/contact/`, name: 'Contact' },
    ],
  }),
])

const state = reactive<Partial<ContactFormSchemaType>>({
  email: '',
  message: '',
  name: '',
})

const pending = ref(false)
const success = ref(false)
const formError = ref<null | string>(null)
const { trackEvent } = useAnalytics()

const isFormValid = computed(() => ContactFormSchema.safeParse(state).success)

function handleKaweahTechClick() {
  trackEvent('external_link_clicked', {
    destination: 'kaweah_tech',
    source: 'contact_page',
  })
}

async function onSubmit(event: FormSubmitEvent<ContactFormSchemaType>) {
  pending.value = true
  formError.value = null
  trackEvent('contact_form_submitted')

  try {
    await $fetch('/api/contact', {
      body: event.data,
      method: 'POST',
    })
    success.value = true
    trackEvent('contact_form_success')
  } catch {
    formError.value = contact.value?.form.errorMessage ?? 'Something went wrong. Please try again.'
    trackEvent('contact_form_error')
  } finally {
    pending.value = false
  }
}
</script>

<template>
  <div v-if="contact">
    <!-- ===== Hero ===== -->
    <SfgSheet>
      <div class="mx-auto max-w-3xl">
        <SfgEyebrow variant="inverted">
          {{ contact.hero.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="1" class="text-brand-chalk mt-5">
          {{ contact.hero.heading }}
        </SfgHeading>
        <p class="font-body text-body mt-6 text-brand-chalk">
          {{ contact.hero.description }}
        </p>
      </div>
    </SfgSheet>

    <!-- ===== Form ===== -->
    <SfgPaper>
      <div class="mx-auto max-w-xl">
        <!-- Success -->
        <div v-if="success" class="flex flex-col items-center gap-6 py-16 text-center">
          <SfgFlame
            :size="96"
            :disk="false"
            body-color="red"
            flame-color="yellow"
            :tilt="2"
          />
          <SfgHeading :level="2">
            {{ contact.form.success.title }}
          </SfgHeading>
          <p class="font-body text-body">
            {{ contact.form.success.description }}
          </p>
          <SfgButton
            variant="primary"
            size="md"
            :to="contact.form.success.backTo"
          >
            {{ contact.form.success.backLabel }}
          </SfgButton>
        </div>

        <!-- Form -->
        <UForm
          v-else
          :schema="ContactFormSchema"
          :state="state"
          class="flex flex-col gap-5"
          @submit="onSubmit"
        >
          <UFormField
            :label="contact.form.fields.name.label"
            name="name"
            required
          >
            <UInput
              v-model="state.name"
              :placeholder="contact.form.fields.name.placeholder"
              class="w-full"
              size="lg"
            />
          </UFormField>

          <UFormField
            :label="contact.form.fields.email.label"
            name="email"
            required
          >
            <UInput
              v-model="state.email"
              :placeholder="contact.form.fields.email.placeholder"
              class="w-full"
              size="lg"
              type="email"
            />
          </UFormField>

          <UFormField
            :label="contact.form.fields.message.label"
            name="message"
            required
          >
            <UTextarea
              v-model="state.message"
              :placeholder="contact.form.fields.message.placeholder"
              :rows="7"
              class="w-full"
              size="lg"
            />
          </UFormField>

          <UAlert
            v-if="formError"
            :description="formError"
            color="error"
            icon="mdi:alert-circle-outline"
            variant="soft"
          />

          <SfgButton
            :disabled="!isFormValid || pending"
            :loading="pending"
            class="self-start"
            size="lg"
            type="submit"
            variant="primary"
          >
            {{ contact.form.submit }}
          </SfgButton>
        </UForm>
      </div>
    </SfgPaper>

    <!-- ===== Kaweah credit ===== -->
    <SfgPaper v-if="!success" :padded="false">
      <div class="mx-auto max-w-xl px-6 pb-24 sm:px-10">
        <div class="flex flex-col gap-4 p-6 rounded-card border-2 border-brand-ink bg-brand-chalk shadow-[0_5px_0_var(--color-brand-ink)]">
          <SfgEyebrow variant="inverted" class="self-start">
            {{ contact.credit.heading }}
          </SfgEyebrow>
          <p class="font-body text-body-sm">
            {{ contact.credit.body }}
          </p>
          <a
            :href="contact.credit.linkTo"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 self-start font-display uppercase text-[12px] tracking-[0.14em] text-brand-red hover:text-brand-ink transition-colors"
            @click="handleKaweahTechClick"
          >
            {{ contact.credit.linkLabel }} →
          </a>
        </div>
      </div>
    </SfgPaper>
  </div>
</template>
